import React, { useCallback, useRef } from "react";
import { pathOr, propOr } from "ramda";
import { Link } from "react-router-dom";
import AsyncSelect from "@bhunjadi/react-select-v3/async";

import { Checkbox, Textbox } from "src/main_app/components/form_elements";
import { getGroupAvailableCustomers } from "src/main_app/services/apis";

// Same fallback chain the group list uses, so a unit reads the same in both places.
const optionLabel = (customer) =>
  propOr("", "trading_name", customer) ||
  propOr("", "owner_name", customer) ||
  propOr("", "site_name", customer) ||
  propOr("", "name", customer) ||
  propOr("", "address", customer) ||
  `#${propOr("", "id", customer)}`;

const toOption = (customer) => ({
  value: customer.id,
  label: optionLabel(customer),
});

/**
 * Multi-unit group settings on the customer Overview tab: flag this customer as
 * the main meter of a group and pick the customers sitting behind that meter.
 */
const CustomerGroupSection = ({
  customerId,
  canEditCustomer,
  groupData,
  onGroupChange,
}) => {
  const isGroupParent = propOr(false, "is_group_parent", groupData);
  const units = propOr([], "units", groupData);
  const nameSuffix = propOr("", "group_name_suffix", groupData);

  const loadOptions = useCallback(
    async (inputValue) => {
      try {
        const response = await getGroupAvailableCustomers(customerId, {
          search: inputValue,
          limit: 50,
        });
        return pathOr([], ["data", "data"], response).map(toOption);
      } catch (error) {
        console.log("error", error);
        return [];
      }
    },
    [customerId]
  );

  // react-select wipes the search box and closes the menu on every pick. Hand
  // the text back on "set-value" so a whole run of units can be added from one
  // search, and reset it once the menu actually closes.
  const searchRef = useRef("");
  const onInputChange = useCallback((value, { action }) => {
    if (action === "set-value") return searchRef.current;
    searchRef.current = action === "input-change" ? value : "";
    return value;
  }, []);

  const onToggle = useCallback(
    (event) => {
      const checked = event.target.checked;
      onGroupChange({
        is_group_parent: checked,
        // Dropping the flag detaches every unit, which is what gets saved.
        units: checked ? units : [],
        group_name_suffix: checked ? nameSuffix : "",
      });
    },
    [onGroupChange, units, nameSuffix]
  );

  const onUnitsChange = useCallback(
    (selected) => {
      onGroupChange({
        is_group_parent: true,
        units: (selected || []).map((option) => ({
          id: option.value,
          name: option.label,
        })),
        group_name_suffix: nameSuffix,
      });
    },
    [onGroupChange, nameSuffix]
  );

  const onNameSuffixChange = useCallback(
    (event) => {
      onGroupChange({
        is_group_parent: true,
        units,
        group_name_suffix: event.target.value,
      });
    },
    [onGroupChange, units]
  );

  return (
    <React.Fragment>
      <h3 className="sub-title mt-4">Multi-unit group</h3>
      <div className="row">
        <div className="col-md-12">
          <Checkbox
            controlId="is_group_parent"
            name="is_group_parent"
            label="This customer is the main meter of a multi-unit group"
            checked={isGroupParent === true}
            onChange={canEditCustomer ? onToggle : () => {}}
          />
        </div>
      </div>

      {isGroupParent && (
        <div className="row mt-2">
          <div className="col-md-12">
            <div className="form-group">
              <label>Units behind this meter</label>
              <AsyncSelect
                isMulti
                cacheOptions
                isDisabled={!canEditCustomer}
                placeholder={
                  <div>Search customers to add them as units of this group</div>
                }
                defaultOptions
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                loadOptions={loadOptions}
                onInputChange={onInputChange}
                value={units.map((unit) => ({
                  value: unit.id,
                  label: unit.name,
                }))}
                onChange={onUnitsChange}
              />
              <small className="text-muted">
                Only customers in this tenant that are not a main meter of
                another group can be added.
              </small>
            </div>

            <div className="form-group">
              <Textbox
                controlId="group_name_suffix"
                name="group_name_suffix"
                label="Shared name suffix"
                placeholder="e.g. 18 Parry Street"
                value={nameSuffix}
                onChange={onNameSuffixChange}
                disabled={!canEditCustomer}
              >
                <small className="text-muted">
                  Removed from the end of every unit name on the group screens.
                  Units behind one meter usually repeat the building address, so
                  there is no room left for the part that tells them apart — set
                  this to "18 Parry Street" and "Unit 1, 18 Parry Street" is
                  shown as "Unit 1". Matching ignores case and spacing. The main
                  meter keeps its full name.
                </small>
              </Textbox>
            </div>
            {units.length > 0 && (
              <p className="mb-0">
                <Link className="underline" to={`/customer-group/${customerId}`}>
                  Open the group view
                </Link>
              </p>
            )}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default CustomerGroupSection;
